import React, { FC } from "react";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { FeatureGate } from "../../../../branding/FeatureGate";
import {
  PaymentButtonSlot,
  PaymentFooter,
} from "../../../../components/PaymentButton";
import { booksCheckoutOverpay } from "../../../../reduxFolder/reducers/books";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  authOverpayFeePercent,
  authSelectUser,
} from "../../../../reduxFolder/selectors/globalSelectors";
import formatter from "../../../../utils/formatter";
import { toFiniteNumber } from "../../../../utils/number";
import { calculatePaymentTotal } from "../../../../utils/paymentFees";
import addOverpayFundsFormik from "../../helpers/addOverpayFundsFormik";
import {
  BooksBalanceLayout,
  BooksBalancePanelSection,
  ContentContainer,
  FormWrapper,
} from "../../styles/BooksAddFunds.style";
import BooksAddPayPalFundsForm from "../BooksAddPayPalFundsPanel/BooksAddPayPalFundsForm";
import BooksBalance from "../BooksBalance/BooksBalance";

interface BooksAddOverpayFundsPanelProps {
  panelData: any;
  booksCheckoutOverpay: ({ amount }: { amount: number }) => Promise<void>;
  authOverpayFeePercent: string;
  openRightPanel: OpenRightPanel;
  user: any;
}

const BooksAddOverpayFundsPanel: FC<BooksAddOverpayFundsPanelProps> =
  React.memo(
    ({
      panelData,
      booksCheckoutOverpay,
      authOverpayFeePercent,
      openRightPanel,
      user,
    }) => {
      const { t } = useTranslation("common");
      const currencySymbol = useCurrencySymbol();
      const restoredValues = panelData.get("formValues");

      const {
        handleSubmit,
        errors,
        values,
        touched,
        isSubmitting,
        isValid,
        setFieldValue,
      } = useFormik(
        addOverpayFundsFormik(
          t,
          booksCheckoutOverpay,
          currencySymbol,
          restoredValues,
        ),
      );
      const { amount } = values;
      const total = calculatePaymentTotal(
        toFiniteNumber(amount),
        authOverpayFeePercent,
      );
      const helpPanelContext = {
        openRightPanel,
        returnPanelType: "BOOKS_ADD_OVERPAY_FUNDS",
        getReturnPanelData: () => panelData.set("formValues", values),
      };

      const formattedTotal = `${currencySymbol}${formatter.currency(total)}`;
      const payBtnTitle = `${t("common.processOverpay")} (${formattedTotal})`;

      return (
        <FeatureGate
          payment="overpay"
          overpayAllowedForUser={user.get("overpay_allowed", false)}
        >
          <ContentWrapper>
            <Heading $isBordered>
              {t("books.addFundsPanel.chargeOverpay")}
            </Heading>
            <ContentContainer>
              <FormWrapper>
                <BooksBalancePanelSection>
                  <BooksBalanceLayout>
                    <BooksBalance balance={user.get("balance")} />
                  </BooksBalanceLayout>
                </BooksBalancePanelSection>
                <BooksAddPayPalFundsForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                />
              </FormWrapper>
              <PaymentButtonSlot>
                <PaymentFooter
                  onClick={() => handleSubmit()}
                  isLoading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                  helpPanelContext={helpPanelContext}
                >
                  {payBtnTitle}
                </PaymentFooter>
              </PaymentButtonSlot>
            </ContentContainer>
          </ContentWrapper>
        </FeatureGate>
      );
    },
  );

const withConnect = connect(
  createStructuredSelector({
    authOverpayFeePercent,
    user: authSelectUser,
  }),
  {
    booksCheckoutOverpay,
    openRightPanel,
  },
);

export default withConnect(BooksAddOverpayFundsPanel);
